import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'
import type { ConflictAlert, SuggestedSlot } from '../../lib/conflicts'
import ConflictTimeline from './ConflictTimeline'

type ConflictAlertCardProps = {
  alert: ConflictAlert
  onReschedule: (slot: SuggestedSlot) => Promise<void>
  onDismiss: () => Promise<void>
}

const ConflictAlertCard = ({ alert, onReschedule, onDismiss }: ConflictAlertCardProps) => {
  const { t } = useTranslation()
  const [pending, setPending] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [customTime, setCustomTime] = useState('')

  const coachSummary = (conflictAlert: ConflictAlert): string => {
    if (conflictAlert.workout && conflictAlert.calendarEvent) {
      return t('coach.conflict.overlapSummary', {
        event: conflictAlert.calendarEvent.title,
        workout: conflictAlert.workout.title,
      })
    }
    return conflictAlert.message
  }

  const handleReschedule = async (slot: SuggestedSlot) => {
    setPending(slot.label)
    setError('')
    try {
      await onReschedule(slot)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('coach.conflict.rescheduleError'))
    } finally {
      setPending(null)
    }
  }

  const handleCustomReschedule = async () => {
    if (!customTime || !alert.workout) return

    const occurrenceDate = alert.occurrence_date ?? alert.workout.startsAt.slice(0, 10)
    const start = new Date(`${occurrenceDate}T${customTime}:00`)
    const durationMs = new Date(alert.workout.endsAt).getTime() - new Date(alert.workout.startsAt).getTime()
    const end = new Date(start.getTime() + Math.max(durationMs, 45 * 60_000))
    const label = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

    await handleReschedule({
      label,
      start: start.toISOString(),
      end: end.toISOString(),
    })
  }

  const handleDismiss = async () => {
    setPending('dismiss')
    setError('')
    try {
      await onDismiss()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('coach.conflict.dismissError'))
    } finally {
      setPending(null)
    }
  }

  const workout = alert.workout
  const calendarEvent = alert.calendarEvent

  return (
    <BentoCard gradient="copper" className="ring-1 ring-amber-900/40">
      <div className="flex items-center gap-2">
        <MaterialIcon name="event_busy" filled size="sm" />
        <p className={`${KILO.meta} !text-amber-600`}>{t('coach.conflict.scheduleConflict')}</p>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-amber-100/90">{coachSummary(alert)}</p>

      {workout && calendarEvent ? (
        <ConflictTimeline
          workout={workout}
          calendarEvent={calendarEvent}
          overlapMinutes={alert.overlapMinutes}
        />
      ) : null}

      {alert.suggested_slots.length > 0 ? (
        <div className="mt-4 flex flex-col gap-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">
            {t('coach.conflict.suggestedTimes')}
          </p>
          {alert.suggested_slots.map((slot) => (
            <button
              key={slot.label}
              type="button"
              disabled={Boolean(pending)}
              className="group flex h-11 items-center justify-center gap-2 rounded-xl bg-kilo-accent text-sm font-bold text-black disabled:opacity-50"
              onClick={() => void handleReschedule(slot)}
            >
              <MaterialIcon name="schedule" hoverable size="sm" />
              {pending === slot.label
                ? t('coach.conflict.rescheduling')
                : t('coach.conflict.moveSessionTo', { time: slot.label })}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-4 space-y-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">
          {t('coach.conflict.customTime')}
        </p>
        <input
          type="time"
          value={customTime}
          onChange={(event) => setCustomTime(event.target.value)}
          className="h-11 w-full rounded-xl border border-kilo-border bg-neutral-900 px-3 text-sm text-white"
        />
        <button
          type="button"
          disabled={!customTime || Boolean(pending)}
          className="group flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-kilo-accent/40 bg-kilo-accent/10 text-sm font-semibold text-kilo-accent disabled:opacity-50"
          onClick={() => void handleCustomReschedule()}
        >
          <MaterialIcon name="edit_calendar" hoverable size="sm" />
          {t('coach.conflict.useCustomTime')}
        </button>
      </div>

      <button
        type="button"
        disabled={Boolean(pending)}
        className="group mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-amber-900/50 bg-neutral-900 text-sm font-semibold text-amber-200 disabled:opacity-50"
        onClick={() => void handleDismiss()}
      >
        <MaterialIcon name="close" hoverable size="sm" />
        {pending === 'dismiss' ? t('coach.conflict.dismissing') : t('coach.conflict.keepCurrentTime')}
      </button>

      {error ? <p className="mt-2 text-sm text-red-400">{error}</p> : null}
    </BentoCard>
  )
}

export default ConflictAlertCard
